import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const Header = () => {
  const { lang, toggleLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home, testid: "nav-home" },
    { to: "/services", label: t.nav.services, testid: "nav-services" },
    { to: "/about", label: t.nav.about, testid: "nav-about" },
    { to: "/blog", label: t.nav.blog, testid: "nav-blog" },
    { to: "/contact", label: t.nav.contact, testid: "nav-contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-rotam-border/60 shadow-sm"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container-rotam flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <span className="text-2xl font-heading font-medium tracking-tight text-rotam-primary">
            ROTAM
          </span>
          <span className="hidden sm:inline-block w-px h-5 bg-rotam-border" />
          <span className="hidden sm:inline-block text-[10px] tracking-[0.25em] uppercase text-rotam-muted">
            Tax & Financial Advisory
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isActive ? "text-rotam-primary font-semibold" : "text-rotam-ink/70 hover:text-rotam-primary"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            onClick={toggleLang}
            className="text-xs tracking-[0.2em] uppercase font-semibold text-rotam-primary border border-rotam-primary/30 rounded-full px-3 py-1.5 hover:bg-rotam-primary hover:text-white transition-all"
          >
            {lang === "tr" ? "EN" : "TR"}
          </button>
          <Link
            to="/contact"
            data-testid="header-cta"
            className="hidden lg:inline-block bg-rotam-primary text-white text-sm px-5 py-2.5 rounded-sm hover:bg-rotam-primary-hover transition-colors"
          >
            {t.nav.cta}
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-rotam-primary p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-rotam-border">
          <div className="container-rotam py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`m-${l.testid}`}
                className={({ isActive }) =>
                  `text-base py-2 ${isActive ? "text-rotam-primary font-semibold" : "text-rotam-ink/80"}`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex items-center gap-3 pt-4 border-t border-rotam-border">
              <button
                data-testid="m-lang-toggle"
                onClick={toggleLang}
                className="text-xs tracking-[0.2em] uppercase font-semibold text-rotam-primary border border-rotam-primary/30 rounded-full px-3 py-1.5"
              >
                {lang === "tr" ? "EN" : "TR"}
              </button>
              <Link
                to="/contact"
                data-testid="m-header-cta"
                className="flex-1 text-center bg-rotam-primary text-white text-sm px-5 py-2.5 rounded-sm"
              >
                {t.nav.cta}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
